import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Heart, Gift, Check } from "lucide-react";
import { format } from "date-fns";
import { fetchRegistry, startCheckout, getPaymentStatus } from "@/lib/api";
import { PRIORITIES } from "@/data/marketplace";

const PRESETS = [25, 50, 100];

export default function GuestRegistry() {
  const { slug } = useParams();
  const [params, setParams] = useSearchParams();
  const [reg, setReg] = useState(null);
  const [err, setErr] = useState("");
  const [amount, setAmount] = useState(50);
  const [giver, setGiver] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [paidBanner, setPaidBanner] = useState(null);

  const load = async () => {
    try {
      const data = await fetchRegistry(slug);
      setReg(data);
    } catch (e) {
      setErr("Registry not found.");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  useEffect(() => {
    const paid = params.get("paid");
    const sid = params.get("session_id");
    if (paid === "cancel") setPaidBanner({ ok: false, text: "Contribution cancelled." });
    if (paid === "success" && sid) {
      let tries = 0;
      const poll = async () => {
        tries++;
        try {
          const s = await getPaymentStatus(sid);
          if (s.payment_status === "paid") {
            setPaidBanner({ ok: true, text: `Thank you ${s.giver || ""}! Your contribution was received.` });
            load();
            return;
          }
        } catch (_) {}
        if (tries < 10) setTimeout(poll, 1500);
      };
      poll();
      const p = new URLSearchParams(params); p.delete("session_id"); setParams(p, { replace: true });
    }
    // eslint-disable-next-line
  }, []);

  if (err) return <div className="p-16 text-center text-[#5C6B62]">{err}</div>;
  if (!reg) return <div className="p-16 text-center text-[#5C6B62]">Loading her registry…</div>;

  const submit = async () => {
    setSubmitting(true);
    try {
      const { checkout_url } = await startCheckout({
        slug, amount: Number(amount),
        giver: giver.trim() || "A dear friend",
        message: message.trim(),
      });
      window.location.href = checkout_url;
    } catch (e) {
      setSubmitting(false);
      alert("Could not start checkout. Please try again.");
    }
  };

  return (
    <div className="bg-[#FDFBF7] min-h-screen">
      <section className="border-b border-[#E5E1D8] bg-[#F4F1EA]/50">
        <div className="mx-auto max-w-4xl px-6 md:px-10 py-14 md:py-20">
          <span className="text-xs tracking-[0.25em] uppercase text-[#5C6B62]">A public care registry</span>
          <h1 className="font-display text-4xl md:text-6xl text-[#2D3732] mt-4 leading-[1.05]">
            A gentle way<br /><span className="italic text-[#D98A71]">to show up.</span>
          </h1>
          {(reg.mother_first_name || reg.city) && (
            <p data-testid="mother-location" className="mt-6 text-sm text-[#5C6B62]">
              {reg.mother_first_name ? `${reg.mother_first_name}'s registry` : "Her registry"}
              {reg.city ? ` · based in ${reg.city}` : ""}
            </p>
          )}
          {reg.profile?.dueDate && (
            <p className="mt-2 text-sm text-[#5C6B62]">Due {format(new Date(reg.profile.dueDate), "d MMM yyyy")}</p>
          )}
          {reg.profile?.priorities?.length > 0 && (
            <p className="mt-2 text-sm text-[#5C6B62]">
              She's asked for: {reg.profile.priorities.map((p) => PRIORITIES.find((x) => x.id === p)?.label).filter(Boolean).join(" · ")}
            </p>
          )}
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-6 md:px-10 py-14">
        {paidBanner && (
          <div className={`mb-8 rounded-2xl px-5 py-4 text-sm border ${paidBanner.ok ? "bg-[#A3B19B]/15 border-[#A3B19B] text-[#2D3732]" : "bg-[#F4F1EA] border-[#E5E1D8] text-[#5C6B62]"}`}>
            {paidBanner.text}
          </div>
        )}

        <div className="rounded-3xl bg-[#FDFBF7] border border-[#E5E1D8] p-6 md:p-8">
          <p className="text-[10px] tracking-[0.25em] uppercase text-[#5C6B62]">Contribute to her Care Fund</p>
          <h2 className="font-display text-3xl md:text-4xl text-[#2D3732] mt-2 leading-tight">
            Any amount<br /><span className="italic text-[#D98A71]">becomes real care.</span>
          </h2>
          <p className="text-sm text-[#5C6B62] mt-3 max-w-md">
            Every dollar goes toward the wishlist she's built. Amounts stay private between you and her.
          </p>

          <div className="mt-8">
            <label className="text-xs tracking-[0.2em] uppercase text-[#5C6B62]">Amount (NZD)</label>
            <div className="mt-3 flex flex-wrap gap-2">
              {PRESETS.map((p) => (
                <button key={p} data-testid={`amount-${p}`} onClick={() => setAmount(p)}
                  className={`rounded-full px-5 py-2.5 text-sm border transition-colors ${amount === p ? "bg-[#2D3732] text-[#FDFBF7] border-[#2D3732]" : "border-[#E5E1D8] text-[#2D3732] hover:border-[#2D3732]"}`}>
                  ${p}
                </button>
              ))}
            </div>
            <input type="number" min={1} value={amount} onChange={(e) => setAmount(Number(e.target.value))}
              data-testid="amount-custom"
              placeholder="Or any custom amount"
              className="mt-3 w-full h-12 rounded-2xl border border-[#E5E1D8] bg-white/60 px-4 focus:outline-none focus:border-[#D98A71] transition-colors" />
          </div>

          <div className="mt-5">
            <label className="text-xs tracking-[0.2em] uppercase text-[#5C6B62]">Your name</label>
            <input type="text" value={giver} onChange={(e) => setGiver(e.target.value)}
              data-testid="giver-name" placeholder="e.g. Sarah & James"
              className="mt-3 w-full h-12 rounded-2xl border border-[#E5E1D8] bg-white/60 px-4 focus:outline-none focus:border-[#D98A71] transition-colors placeholder:text-[#5C6B62]/50" />
          </div>
          <div className="mt-5">
            <label className="text-xs tracking-[0.2em] uppercase text-[#5C6B62]">A short note (optional)</label>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={2}
              data-testid="giver-note" placeholder="Thinking of you."
              className="mt-3 w-full rounded-2xl border border-[#E5E1D8] bg-white/60 px-4 py-3 focus:outline-none focus:border-[#D98A71] transition-colors placeholder:text-[#5C6B62]/50 resize-none" />
          </div>

          <FeeBreakdown amount={Number(amount) || 0} />

          <button data-testid="complete-contribution" disabled={submitting || amount < 1} onClick={submit}
            className="mt-6 w-full rounded-full bg-[#D98A71] hover:bg-[#c67a63] text-[#FDFBF7] py-4 text-sm font-medium transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2">
            <Heart size={16} strokeWidth={1.5} />
            {submitting ? "Redirecting…" : `Complete Contribution — $${(Number(amount) * 1.15).toFixed(2)}`}
          </button>
          <p className="mt-3 text-center text-[11px] text-[#5C6B62]">Secure checkout by Stripe · Test Mode</p>
        </div>

        <div className="mt-12">
          <p className="text-xs tracking-[0.25em] uppercase text-[#5C6B62]">On her wishlist</p>
          <div className="mt-6 grid gap-5 md:grid-cols-2">
            {reg.items.map((item) => (
              <article key={item.id} className="bg-[#FDFBF7] border border-[#E5E1D8] rounded-3xl overflow-hidden flex">
                <img src={item.image} alt="" className="w-32 h-full object-cover flex-shrink-0" />
                <div className="p-5 flex-1 min-w-0">
                  <p className="text-[11px] text-[#5C6B62]">{item.tag}</p>
                  <h3 className="font-display text-lg text-[#2D3732] mt-1 leading-tight">{item.title}</h3>
                  <p className="text-xs text-[#5C6B62] mt-2 line-clamp-3">{item.description}</p>
                </div>
              </article>
            ))}
          </div>
        </div>

        {reg.givers?.length > 0 && (
          <div className="mt-10 rounded-3xl bg-[#F4F1EA]/60 border border-[#E5E1D8] p-6 md:p-8">
            <div className="flex items-center gap-3">
              <Gift size={16} strokeWidth={1.5} className="text-[#D98A71]" />
              <p className="text-[10px] tracking-[0.25em] uppercase text-[#5C6B62]">Gifted by</p>
            </div>
            <ul className="mt-5 divide-y divide-[#E5E1D8]">
              {reg.givers.map((g, i) => (
                <li key={i} className="py-3 flex items-center gap-4 text-sm">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-[#2D3732] truncate">{g.giver}</p>
                    {g.message && <p className="text-xs italic text-[#5C6B62] truncate">"{g.message}"</p>}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>
    </div>
  );
}


const FeeBreakdown = ({ amount }) => {
  const principal = Math.max(0, Number(amount) || 0);
  const fee = Math.round(principal * 0.15 * 100) / 100;
  const total = Math.round((principal + fee) * 100) / 100;
  if (principal <= 0) return null;

  return (
    <div
      data-testid="fee-breakdown"
      className="mt-6 rounded-2xl bg-[#F4F1EA]/70 border border-[#E5E1D8] p-5 text-sm"
    >
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <p className="text-[#2D3732]">Contribution to Mum's Care Fund</p>
          <p className="text-[11px] text-[#5C6B62] mt-0.5">100% goes to her fund</p>
        </div>
        <span data-testid="fee-principal" className="font-display text-lg text-[#2D3732]">
          ${principal.toFixed(2)}
        </span>
      </div>
      <div className="mt-3 flex items-baseline justify-between gap-3">
        <div>
          <p className="text-[#2D3732]">Held Concierge &amp; Service Fee (15%)</p>
          <p className="text-[11px] text-[#5C6B62] mt-0.5">
            Supports vendor coordination, delivery and platform care
          </p>
        </div>
        <span data-testid="fee-service" className="font-display text-lg text-[#2D3732]">
          ${fee.toFixed(2)}
        </span>
      </div>
      <div className="mt-4 pt-4 border-t border-[#E5E1D8] flex items-baseline justify-between gap-3">
        <p className="text-xs tracking-[0.2em] uppercase text-[#5C6B62]">Total charged today</p>
        <span data-testid="fee-total" className="font-display text-2xl text-[#D98A71]">
          ${total.toFixed(2)}
        </span>
      </div>
    </div>
  );
};
